import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import EventCard from "@/components/feature/EventCard";
import { useSeo } from "@/hooks/useSeo";
import { categories, events, hotTopics } from "@/mocks/site";

type SortKey = "heat" | "date";

export default function Archive() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("category") ?? "all";
  const query = searchParams.get("q") ?? "";
  const [term, setTerm] = useState(query);
  const [sort, setSort] = useState<SortKey>("heat");

  useEffect(() => {
    setTerm(query);
  }, [query]);

  const activeCategory = categories.find((c) => c.slug === activeCat) ?? categories[0];

  const filtered = useMemo(() => {
    const kw = query.trim().toLowerCase();
    const list = events.filter((event) => {
      const matchCat = activeCat === "all" || event.categorySlug === activeCat;
      if (!kw) return matchCat;
      const haystack = `${event.title} ${event.excerpt} ${event.category} ${event.tags.join(" ")} ${event.author}`.toLowerCase();
      return matchCat && haystack.includes(kw);
    });
    return [...list].sort((a, b) => (sort === "heat" ? b.heat - a.heat : b.date.localeCompare(a.date)));
  }, [activeCat, query, sort]);

  useSeo({
    title: `${activeCategory.name} - 事件档案列表 | 独家吃瓜`,
    description: `独家吃瓜${activeCategory.name}档案列表，共收录 ${events.length} 条事件复盘。按时间线梳理内幕、细节线索与各方回应，帮你快速找到想追的那条线。`,
    keywords: "独家吃瓜档案,事件复盘列表,独家吃瓜深扒,独家吃瓜内幕,独家吃瓜时间线",
    canonicalPath: "/archive",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          name: `${activeCategory.name} - 事件档案`,
          description: activeCategory.desc,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "/" },
            { "@type": "ListItem", position: 2, name: "事件档案", item: "/archive" },
          ],
        },
        {
          "@type": "ItemList",
          numberOfItems: filtered.length,
          itemListElement: filtered.map((event, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: event.title,
            url: `/archive/${event.slug}`,
          })),
        },
      ],
    },
  });

  const applyCategory = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    if (slug === "all") next.delete("category");
    else next.set("category", slug);
    setSearchParams(next);
  };

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    const value = term.trim();
    if (value) next.set("q", value);
    else next.delete("q");
    setSearchParams(next);
  };

  const clearAll = () => {
    setSearchParams(new URLSearchParams());
    setTerm("");
  };

  const hasFilter = activeCat !== "all" || Boolean(query);

  return (
    <>
      <SiteHeader />
      <main>
        <div className="border-b border-background-200 bg-background-100">
          <div className="container-x py-8 md:py-12">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "事件档案", to: "/archive" }, ...(activeCat !== "all" ? [{ label: activeCategory.name }] : [])]} />
            <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              事件档案库
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-foreground-600">
              共归档 {events.length} 条事件复盘。每个条目都包含时间线、线索要点与分节正文，按发生顺序排列，方便你从头核对一遍。
            </p>

            <form onSubmit={submitSearch} className="mt-6 flex max-w-2xl items-center gap-2" data-readdy-form="archive-search">
              <div className="flex h-11 flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
                <i className="ri-search-line text-base text-foreground-500"></i>
                <input
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder="搜索事件标题、标签或作者"
                  className="h-full w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
                />
              </div>
              <button
                type="submit"
                className="h-11 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
              {hasFilter && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="h-11 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm text-foreground-700 transition-colors hover:bg-background-50"
                >
                  重置
                </button>
              )}
            </form>
          </div>
        </div>

        <div className="container-x py-10 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-24">
                <h2 className="font-heading text-sm font-bold tracking-wide text-foreground-950">主题分类</h2>
                <ul className="mt-4 space-y-1">
                  {categories.map((cat) => {
                    const count =
                      cat.slug === "all"
                        ? events.length
                        : events.filter((e) => e.categorySlug === cat.slug).length;
                    const active = cat.slug === activeCat;
                    return (
                      <li key={cat.slug}>
                        <button
                          type="button"
                          onClick={() => applyCategory(cat.slug)}
                          className={`flex w-full cursor-pointer items-center justify-between gap-3 whitespace-nowrap rounded-md px-3 py-2.5 text-left text-sm transition-colors ${
                            active
                              ? "bg-primary-500 text-background-50"
                              : "text-foreground-700 hover:bg-background-100"
                          }`}
                        >
                          <span className="font-medium">{cat.name}</span>
                          <span className={active ? "text-background-200" : "text-foreground-400"}>{count}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>

                <h2 className="mt-8 font-heading text-sm font-bold tracking-wide text-foreground-950">正在复盘</h2>
                <ul className="mt-4 flex flex-wrap gap-2">
                  {hotTopics.map((topic) => (
                    <li key={topic.slug}>
                      <Link
                        to={`/archive/${topic.slug}`}
                        className="inline-block rounded-full bg-secondary-50 px-3 py-1.5 text-xs text-secondary-800 transition-colors hover:bg-secondary-100"
                      >
                        {topic.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            <div className="lg:col-span-9">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <p className="text-sm text-foreground-600">
                  找到 <span className="font-bold text-foreground-950">{filtered.length}</span> 条
                  {query && (
                    <span>
                      ，关键词「<span className="font-medium text-primary-600">{query}</span>」
                    </span>
                  )}
                </p>
                <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
                  <button
                    type="button"
                    onClick={() => setSort("heat")}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                      sort === "heat" ? "bg-background-50 text-foreground-950" : "text-foreground-600"
                    }`}
                  >
                    按热度
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort("date")}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                      sort === "date" ? "bg-background-50 text-foreground-950" : "text-foreground-600"
                    }`}
                  >
                    按时间
                  </button>
                </div>
              </div>

              {filtered.length > 0 ? (
                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
                  {filtered.map((event, i) => (
                    <EventCard key={event.slug} event={event} index={i} />
                  ))}
                </div>
              ) : (
                <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-500">
                    <i className="ri-emotion-unhappy-line text-2xl"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                    没有匹配的档案
                  </h3>
                  <p className="mx-auto mt-2 max-w-md text-sm leading-7 text-foreground-600">
                    试试换个关键词，或者取消分类筛选看看全部档案。如果这是一条我们还没做过的事件，欢迎把它投递给我们。
                  </p>
                  <div className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row">
                    <button
                      type="button"
                      onClick={clearAll}
                      className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      查看全部档案
                    </button>
                    <Link
                      to="/about#contact"
                      className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
                    >
                      投递这条线索
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}